import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CircleNotch,
  Buildings,
  ChartPie,
  Plus,
  X,
  Prohibit,
  CalendarBlank,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import { TenantSelect } from '@/components/platform/TenantSelect';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { ScrollLock } from '@/components/shared/ScrollLock';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PlanCount { plan: string; count: number }
interface StatusCount { status: string; count: number }
interface RecentChange {
  id: string;
  plan: string;
  status: string;
  startedAt: string;
  expiresAt: string | null;
  chambers: { id: string; name: string; slug: string } | null;
}

interface Distribution {
  byPlan: PlanCount[];
  byStatus: StatusCount[];
  recentChanges: RecentChange[];
}

interface Subscription {
  id: string;
  plan: string;
  status: string;
  startedAt: string;
  expiresAt: string | null;
  cancelledAt: string | null;
  notes: string | null;
  createdAt: string;
  chambers: { id: string; name: string; slug: string; plan: string; status: string } | null;
}

interface SubscriptionsResponse {
  data: Subscription[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(str: string | null) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function PlanBadge({ plan }: { plan: string }) {
  const map: Record<string, string> = {
    trial: 'bg-plat-warning-soft text-plat-warning-text',
    starter: 'bg-plat-info-soft text-plat-info-text',
    pro: 'bg-plat-indigo-soft text-plat-indigo-text',
    enterprise: 'bg-plat-purple-soft text-plat-purple-text',
  };
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${map[plan] ?? 'bg-plat-neutral-soft text-plat-secondary'}`}>
      {plan}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: 'bg-plat-success-soft text-plat-success-text',
    cancelled: 'bg-plat-danger-soft text-plat-danger-text',
    expired: 'bg-plat-neutral-soft text-plat-faint',
  };
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${map[status] ?? 'bg-plat-neutral-soft text-plat-secondary'}`}>
      {status}
    </span>
  );
}

const PLANS = ['trial', 'starter', 'pro', 'enterprise'] as const;
const PLAN_COLOURS: Record<string, string> = {
  trial: 'bg-plat-warning-text', starter: 'bg-plat-info-text', pro: 'bg-plat-indigo-text', enterprise: 'bg-plat-purple-text',
};

// ── Create subscription modal ──────────────────────────────────────────────────

function CreateSubscriptionModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [chambersId, setChambersId] = useState('');
  const [plan, setPlan] = useState<string>('starter');
  const [notes, setNotes] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      platformApi.post('/subscriptions', {
        chambersId,
        plan,
        notes: notes || undefined,
        startedAt: new Date().toISOString(),
      }),
    onSuccess: () => {
      toast.success('Subscription record created and plan synced');
      onSaved();
      onClose();
    },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/60 px-4">
      <ScrollLock />
      <div className="my-auto w-full max-w-md rounded-xl border border-plat-border bg-plat-surface p-6 shadow-2xl">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-[15px] font-semibold text-plat-fg">Record plan change</h3>
          <button onClick={onClose} className="text-plat-muted hover:text-plat-secondary"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Organisation</label>
            <TenantSelect value={chambersId} onChange={setChambersId} placeholder="Select organisation…" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary">New plan</label>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-secondary focus:border-plat-focus focus:outline-none"
            >
              {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-plat-border bg-plat-bg p-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none"
              placeholder="e.g. Upgraded after demo call"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-secondary hover:bg-plat-hover">Cancel</button>
            <button
              onClick={() => mutation.mutate()}
              disabled={!chambersId || mutation.isPending}
              className="rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:bg-plat-neutral-soft disabled:cursor-not-allowed disabled:text-plat-disabled"
            >
              {mutation.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Cancel / expiry dialog ─────────────────────────────────────────────────────

function SubscriptionActionDialog({
  target, onClose, onConfirm, pending,
}: {
  target: { sub: Subscription; mode: 'cancel' | 'expiry' };
  onClose: () => void;
  onConfirm: (body: Record<string, unknown>) => void;
  pending: boolean;
}) {
  const { sub, mode } = target;
  const isCancel = mode === 'cancel';
  const [expiry, setExpiry] = useState(sub.expiresAt ? sub.expiresAt.slice(0, 10) : '');

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/60 px-4">
      <ScrollLock />
      <div className="my-auto w-full max-w-md rounded-xl border border-plat-border bg-plat-surface p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-[15px] font-semibold text-plat-fg">{isCancel ? 'Cancel subscription' : 'Set expiry date'}</h3>
          <button onClick={onClose} className="text-plat-muted hover:text-plat-secondary"><X className="h-4 w-4" /></button>
        </div>
        <p className="mb-4 text-sm text-plat-secondary">{sub.chambers?.name ?? 'This organisation'} — <span className="capitalize">{sub.plan}</span> plan.</p>
        {isCancel ? (
          <p className="mb-5 rounded-lg border border-plat-danger-line bg-plat-danger-soft px-3 py-3 text-sm text-plat-danger-text">
            This marks the subscription cancelled as of today. The organisation keeps its current plan until you change it, and you can reactivate later.
          </p>
        ) : (
          <div className="mb-5">
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Expiry date</label>
            <input
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none [color-scheme:dark]"
            />
            <p className="mt-1.5 text-xs text-plat-faint">Leave blank to clear the expiry (no end date).</p>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-secondary hover:bg-plat-hover">Close</button>
          <button
            onClick={() =>
              onConfirm(
                isCancel
                  ? { status: 'cancelled', cancelledAt: new Date().toISOString() }
                  : { expiresAt: expiry ? new Date(expiry).toISOString() : null },
              )
            }
            disabled={pending}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-plat-fg disabled:cursor-not-allowed disabled:text-plat-disabled',
              isCancel ? 'bg-plat-danger-solid hover:bg-plat-danger-solid-hover' : 'bg-plat-accent-solid hover:bg-plat-accent-solid-hover',
            )}
          >
            {pending ? <CircleNotch className="h-4 w-4 animate-spin" /> : isCancel ? 'Cancel subscription' : 'Save expiry'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PlatformBillingPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [filterChambersId, setFilterChambersId] = useState('');
  const [actionTarget, setActionTarget] = useState<{ sub: Subscription; mode: 'cancel' | 'expiry' } | null>(null);

  useEffect(() => { setPage(1); }, [filterChambersId]);

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      platformApi.patch(`/subscriptions/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform', 'subscriptions'] });
      toast.success('Subscription updated');
    },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  const reactivate = (sub: Subscription) =>
    updateMutation.mutate({ id: sub.id, body: { status: 'active', cancelledAt: null } });

  const distQ = useQuery<Distribution>({
    queryKey: ['platform', 'subscriptions', 'distribution'],
    queryFn: async () => {
      const res = await platformApi.get('/subscriptions/distribution');
      return res.data?.data ?? res.data;
    },
    refetchInterval: 60_000,
  });

  const listQ = useQuery<SubscriptionsResponse>({
    queryKey: ['platform', 'subscriptions', page, filterChambersId],
    queryFn: async () => {
      const res = await platformApi.get('/subscriptions', {
        params: { page, limit: 25, chambersId: filterChambersId || undefined },
      });
      return res.data;
    },
  });

  const totalByPlan = distQ.data?.byPlan.reduce((a, b) => a + b.count, 0) || 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-plat-fg">Billing & Subscriptions</h2>
          <p className="mt-0.5 text-sm text-plat-muted">Plan distribution and subscription history across all organisations</p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover"
        >
          <Plus className="h-4 w-4" />
          Record change
        </button>
      </div>

      {distQ.isLoading ? (
        <div className="flex items-center justify-center py-16"><CircleNotch className="h-7 w-7 animate-spin text-plat-info-text" /></div>
      ) : distQ.data && (
        <>
          {/* Plan distribution */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
              <div className="mb-4 flex items-center gap-2">
                <ChartPie className="h-4 w-4 text-plat-muted" />
                <h3 className="text-sm font-semibold text-plat-fg">By plan</h3>
              </div>
              <div className="space-y-3">
                {distQ.data.byPlan.map((p) => (
                  <div key={p.plan}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className={`h-2.5 w-2.5 rounded-full ${PLAN_COLOURS[p.plan] ?? 'bg-plat-neutral-text'}`} />
                        <span className="capitalize text-plat-secondary">{p.plan}</span>
                      </div>
                      <span className="text-plat-secondary">{p.count}</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-plat-border">
                      <div
                        className={`h-full rounded-full ${PLAN_COLOURS[p.plan] ?? 'bg-plat-neutral-text'}`}
                        style={{ width: `${(p.count / totalByPlan) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
              <div className="mb-4 flex items-center gap-2">
                <Buildings className="h-4 w-4 text-plat-muted" />
                <h3 className="text-sm font-semibold text-plat-fg">By status</h3>
              </div>
              <div className="space-y-2">
                {distQ.data.byStatus.map((s) => (
                  <div key={s.status} className="flex items-center justify-between rounded-lg bg-plat-border/50 px-3 py-2">
                    <span className="text-sm capitalize text-plat-secondary">{s.status}</span>
                    <span className="text-sm font-semibold text-plat-fg">{s.count}</span>
                  </div>
                ))}
              </div>

              {distQ.data.recentChanges.length > 0 && (
                <div className="mt-4">
                  <div className="mb-2 text-xs font-bold uppercase tracking-wider text-plat-faint">Recent changes</div>
                  <div className="space-y-2">
                    {distQ.data.recentChanges.slice(0, 5).map((c) => (
                      <div key={c.id} className="flex items-center justify-between text-xs">
                        <span className="text-plat-secondary truncate max-w-[160px]">{c.chambers?.name ?? '—'}</span>
                        <div className="flex items-center gap-2">
                          <PlanBadge plan={c.plan} />
                          <span className="text-plat-faint">{fmt(c.startedAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Subscription log */}
          <div>
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-plat-fg">Subscription history</h3>
              <div className="w-64">
                <TenantSelect value={filterChambersId} onChange={setFilterChambersId} includeAll allLabel="All organisations" />
              </div>
            </div>
            {listQ.isLoading ? (
              <div className="flex items-center justify-center py-10"><CircleNotch className="h-6 w-6 animate-spin text-plat-info-text" /></div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-plat-border bg-plat-surface">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-plat-border">
                      <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Organisation</th>
                      <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Plan</th>
                      <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Status</th>
                      <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Started</th>
                      <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Expires</th>
                      <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Notes</th>
                      <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-plat-border">
                    {(listQ.data?.data ?? []).length === 0 ? (
                      <tr><td colSpan={7} className="py-10 text-center text-sm text-plat-muted">No subscription records yet.</td></tr>
                    ) : (listQ.data?.data ?? []).map((s) => (
                      <tr key={s.id} className="hover:bg-plat-hover">
                        <td className="px-5 py-3.5">
                          <div className="font-medium text-plat-fg">{s.chambers?.name ?? '—'}</div>
                          {s.chambers && <div className="text-[10px] text-plat-faint">{s.chambers.slug}</div>}
                        </td>
                        <td className="px-5 py-3.5"><PlanBadge plan={s.plan} /></td>
                        <td className="px-5 py-3.5"><StatusBadge status={s.status} /></td>
                        <td className="px-5 py-3.5 text-plat-secondary">{fmt(s.startedAt)}</td>
                        <td className="px-5 py-3.5 text-plat-secondary">{fmt(s.expiresAt)}</td>
                        <td className="px-5 py-3.5 text-plat-muted max-w-[200px] truncate">{s.notes || '—'}</td>
                        <td className="px-5 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setActionTarget({ sub: s, mode: 'expiry' })}
                              className="inline-flex items-center gap-1 rounded-md border border-plat-border px-2 py-1 text-xs text-plat-secondary hover:bg-plat-hover"
                            >
                              <CalendarBlank className="h-3.5 w-3.5" /> Expiry
                            </button>
                            {s.status === 'active' ? (
                              <button
                                onClick={() => setActionTarget({ sub: s, mode: 'cancel' })}
                                className="inline-flex items-center gap-1 rounded-md border border-plat-danger-line px-2 py-1 text-xs text-plat-danger-text hover:bg-plat-danger-soft"
                              >
                                <Prohibit className="h-3.5 w-3.5" /> Cancel
                              </button>
                            ) : (
                              <button
                                onClick={() => reactivate(s)}
                                disabled={updateMutation.isPending}
                                className="inline-flex items-center gap-1 rounded-md border border-plat-success-line px-2 py-1 text-xs text-plat-success-text hover:bg-plat-success-soft disabled:cursor-not-allowed disabled:text-plat-disabled"
                              >
                                <ArrowCounterClockwise className="h-3.5 w-3.5" /> Reactivate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(listQ.data?.pagination?.totalPages ?? 0) > 1 && (
              <div className="mt-3 flex items-center justify-between text-sm text-plat-muted">
                <span>Page {page} of {listQ.data?.pagination.totalPages}</span>
                <div className="flex gap-2">
                  <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-plat-border px-3 py-1.5 text-xs text-plat-secondary hover:bg-plat-hover disabled:cursor-not-allowed disabled:text-plat-disabled">Previous</button>
                  <button disabled={page >= (listQ.data?.pagination.totalPages ?? 1)} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-plat-border px-3 py-1.5 text-xs text-plat-secondary hover:bg-plat-hover disabled:cursor-not-allowed disabled:text-plat-disabled">Next</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {createOpen && (
        <CreateSubscriptionModal
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['platform', 'subscriptions'] });
          }}
        />
      )}

      {actionTarget && (
        <SubscriptionActionDialog
          target={actionTarget}
          pending={updateMutation.isPending}
          onClose={() => setActionTarget(null)}
          onConfirm={(body) =>
            updateMutation.mutate(
              { id: actionTarget.sub.id, body },
              { onSuccess: () => setActionTarget(null) },
            )
          }
        />
      )}
    </div>
  );
}
